import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Key } from 'lucide-react';
import { useGameState } from '@/contexts/GameStateContext';
import { useToast } from '@/hooks/use-sonner-toast';

interface FloatingKeyProps {
  onCollect: () => void;
}

const FloatingKey: React.FC<FloatingKeyProps> = ({ onCollect }) => {
  const [animationStage, setAnimationStage] = useState<'grow' | 'fly' | 'complete'>('grow');
  const { incrementStat } = useGameState();
  const { toast } = useToast();

  // Get the position of the Keys counter in the header
  const getKeysCounterPosition = () => {
    // Find all elements with text content "Keys:"
    const keysElements = Array.from(document.querySelectorAll('span')).filter(
      (el) => el.textContent === 'Keys:'
    );

    if (keysElements.length > 0) {
      const keysElement = keysElements[0];
      const rect = keysElement.getBoundingClientRect();
      return {
        x: rect.right + 20, // Position to the right of "Keys:" text
        y: rect.top + rect.height / 2,
      };
    }

    // Fallback position if we can't find the Keys counter
    return { x: 100, y: 50 };
  };

  // Auto-start the fly animation after the grow animation completes
  useEffect(() => {
    if (animationStage === 'grow') {
      const timer = setTimeout(() => {
        setAnimationStage('fly');
      }, 1500); // Wait 1.5 seconds before flying to counter

      return () => clearTimeout(timer);
    }

    if (animationStage === 'fly') {
      const timer = setTimeout(() => {
        setAnimationStage('complete');
        incrementStat('keysCollected');

        toast({
          title: 'Key Collected!',
          description: 'You can now use the Deconstructor once.',
          variant: 'default',
        });

        onCollect();
      }, 800); // Wait for fly animation to complete

      return () => clearTimeout(timer);
    }
  }, [animationStage, incrementStat, onCollect, toast]);

  if (animationStage === 'complete') return null;

  const targetPosition = animationStage === 'fly' ? getKeysCounterPosition() : null;

  return (
    <motion.div
      className="fixed z-50 pointer-events-none"
      style={{
        left: '50%',
        top: '50%',
        x: '-50%',
        y: '-50%',
      }}
      animate={
        animationStage === 'grow'
          ? {
              scale: [0.1, 1.5, 1.2],
              opacity: [0, 1, 1],
              rotate: [0, 10, -10, 0],
            }
          : {
              x: targetPosition ? targetPosition.x - window.innerWidth / 2 : 0,
              y: targetPosition ? targetPosition.y - window.innerHeight / 2 : 0,
              scale: 0.3,
              opacity: 0,
            }
      }
      transition={
        animationStage === 'grow'
          ? {
              duration: 1,
              ease: 'easeOut',
              times: [0, 0.6, 1],
            }
          : {
              duration: 0.8,
              ease: 'easeInOut',
            }
      }
    >
      <div className="relative">
        <div className="absolute inset-0 bg-yellow-400 blur-md opacity-50 rounded-full"></div>
        <div className="relative bg-gradient-to-br from-yellow-300 to-yellow-500 p-3 rounded-lg shadow-lg">
          <Key className="h-8 w-8 text-yellow-900" />
        </div>
      </div>
    </motion.div>
  );
};

export default FloatingKey;
